import { getOptionViewState, RENDER_MODE } from './view_state';
import {
  cloneTemplate,
  applyOptionState,
  applyGroupState,
  applySelectedOptionsState,
  createSubList,
  addNoResultsOptionIfNeeded,
} from './dom_handler';

export const createOptionRenderer = ({ templates, multiple, fieldId, maxSelectedChoices = undefined }) => {
  const _createSimpleOption = (optionData, context) => {
    const state = getOptionViewState(optionData, context);
    if (!state.shouldRender) {
      return null;
    }

    const optionElement = cloneTemplate(templates, 'option');
    applyOptionState(optionElement, state);

    return optionElement;
  };

  const _createOptionForMultipleSelection = (optionData, context) => {
    const state = getOptionViewState(optionData, context);
    if (!state.shouldRender) {
      return null;
    }

    const optionElement = cloneTemplate(templates, 'multiselectOption');
    applyOptionState(optionElement, state);

    const li = optionElement.querySelector('li');
    if (state.children.length > 0) {
      const subList = _addSelectableMultiLevelGroup(optionData, context);
      li.appendChild(subList);
    }

    return optionElement;
  };

  const _addSelectableMultiLevelGroup = (optionData, context) => {
    const subList = createSubList(optionData.display !== false);

    optionData.children.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        subList.appendChild(optionElement);
      }
    });

    return subList;
  };

  const _createOptionsGroup = (optionData, context) => {
    const state = getOptionViewState(optionData, context);
    if (!state.shouldRender) {
      return null;
    }

    const groupElement = cloneTemplate(templates, 'optionGroup');
    const groupTarget = groupElement.querySelector('.option-group__list');

    applyGroupState(groupElement, state);

    optionData.children.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        groupTarget.appendChild(optionElement);
      }
    });

    return groupTarget.hasChildNodes() ? groupElement : null;
  };

  const _addOption = (optionData, context) => {
    if (!optionData.isSelectable && optionData.children) {
      return _createOptionsGroup(optionData, context);
    }

    if (multiple) {
      return _createOptionForMultipleSelection(optionData, context);
    }

    return _createSimpleOption(optionData, context);
  };

  const _addSelectedOptions = (optionData, context) => {
    const optionElement = cloneTemplate(templates, 'selectedOptions');
    const list = optionElement.querySelector('.selected-options__list');

    applySelectedOptionsState(optionElement, context.fieldId);

    const selectedContext = { ...context, renderMode: RENDER_MODE.ONLY_SELECTED };

    optionData.forEach((option) => {
      const childOptionElement = _addOption(option, selectedContext);
      if (childOptionElement) {
        list.appendChild(childOptionElement);
      }
    });

    return optionElement;
  };

  const addOptions = ({ options, target, selectedOptions, isSearching = false, isSearchable = false }) => {
    const fragment = document.createDocumentFragment();
    const shouldRenderSelectedOptions = isSearchable && multiple && selectedOptions.length && !isSearching;

    const context = {
      multiple,
      maxSelectedChoices,
      isSearching,
      fieldId,
      selectedIds: new Set(selectedOptions.map((option) => option.id)),
      selectedCount: selectedOptions.length,
      renderMode: shouldRenderSelectedOptions ? RENDER_MODE.ONLY_UNSELECTED : RENDER_MODE.ALL,
    };

    if (shouldRenderSelectedOptions) {
      const selectedOptionsFragment = _addSelectedOptions(options, context);
      if (selectedOptionsFragment) {
        fragment.appendChild(selectedOptionsFragment);
      }
    }

    options.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        fragment.appendChild(optionElement);
      }
    });

    addNoResultsOptionIfNeeded(fragment, options, isSearching);

    target.appendChild(fragment);
  };

  const showLoading = (target) => {
    const loadingElement = cloneTemplate(templates, 'loading');
    target.appendChild(loadingElement);
  };

  return {
    addOptions,
    showLoading,
  };
};
